interface Point {
  x: number;
  y: number;
}

// Implements SVG arc endpoint-to-center conversion exactly as in the spec:
// https://www.w3.org/TR/SVG11/implnote.html#ArcConversionEndpointToCenter
// Assumes x-axis-rotation = 0
export function calculateEllipseCenter(
  p1: Point,
  p2: Point,
  rx: number,
  ry: number,
  largeArcFlag: 0 | 1,
  sweepFlag: 0 | 1
): { cx: number; cy: number; rx: number; ry: number } {
  let x1 = p1.x;
  let y1 = p1.y;
  let x2 = p2.x;
  let y2 = p2.y;

  // Step 1: Ensure radii are positive
  rx = Math.abs(rx);
  ry = Math.abs(ry);

  // Step 2: Compute (x1', y1')
  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;
  let x1p = dx;
  let y1p = dy;

  // Step 3: Ensure radii are large enough
  const x1p_sq = x1p * x1p;
  const y1p_sq = y1p * y1p;
  let lambda = x1p_sq / (rx * rx) + y1p_sq / (ry * ry);
  if (lambda > 1) {
    const scale = Math.sqrt(lambda);
    rx *= scale;
    ry *= scale;
  }

  // Step 4: Compute (cx', cy')
  const sign = largeArcFlag !== sweepFlag ? 1 : -1;
  const rx_sq = rx * rx;
  const ry_sq = ry * ry;
  const num = rx_sq * ry_sq - rx_sq * y1p_sq - ry_sq * x1p_sq;
  const denom = rx_sq * y1p_sq + ry_sq * x1p_sq;
  let factor = 0;
  if (denom !== 0) {
    factor = sign * Math.sqrt(Math.max(0, num / denom));
  }
  const cxp = (factor * (rx * y1p)) / ry;
  const cyp = (factor * (-ry * x1p)) / rx;

  // Step 5: Compute (cx, cy)
  const cx = cxp + (x1 + x2) / 2;
  const cy = cyp + (y1 + y2) / 2;

  return { cx, cy, rx, ry };
}
